import React, { useState, useMemo, useCallback, useEffect } from "react"
import { useUserResumes } from "../../../hooks/DataFetching/useUserResumes"
import { useSubmitApplication } from "../../../hooks/DataFetching/useSubmitApplication"
import { useCheckApplicationStatus } from "../../../hooks/DataFetching/useCheckApplicationStatus"
import { useCreateResume } from "../../../hooks/DataFetching/useCreateResume"
import styles from "../../../components/ui/dialog.module.css"
import { X } from "lucide-react"
import { StatusMessage } from "../../ui/StatusMessage"

export const ApplicationModal = React.memo(function ApplicationModal({ isOpen, onClose, positionId }) {
  const [selectedResume, setSelectedResume] = useState("")
  const [coverLetter, setCoverLetter] = useState("")
  const [availableResumes, setAvailableResumes] = useState([])
  const [resumeDraft, setResumeDraft] = useState({
    title: "",
    summary: "",
    skills: "",
  })
  const [createResumeResultError, setCreateResumeResultError] = useState(null)

  const memoizedPositionId = useMemo(() => [positionId], [positionId])

  const { resumes, loading: resumesLoading, error: resumesError } = useUserResumes()
  const { submitApplication, submitting, submitError, submitSuccess } = useSubmitApplication()
  const { hasApplied, loading: statusLoading, error: statusError } = useCheckApplicationStatus(memoizedPositionId)
  const { createResume, creatingResume, createResumeError } = useCreateResume()

  useEffect(() => {
    setAvailableResumes(Array.isArray(resumes) ? resumes : [])
  }, [resumes])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      await submitApplication(positionId, selectedResume, coverLetter)
    },
    [submitApplication, positionId, selectedResume, coverLetter],
  )

  const handleResumeDraftChange = useCallback((event) => {
    const { name, value } = event.target
    setResumeDraft((draft) => ({
      ...draft,
      [name]: value,
    }))
  }, [])

  const handleCreateResume = useCallback(
    async (event) => {
      event.preventDefault()
      setCreateResumeResultError(null)

      try {
        const createdResume = await createResume({
          title: resumeDraft.title.trim(),
          summary: resumeDraft.summary.trim(),
          skills: resumeDraft.skills.trim(),
          isPublic: false,
          lookingForWork: true,
        })

        if (!createdResume?.id) {
          setCreateResumeResultError("Resume was created but no resume id was returned.")
          return
        }

        setAvailableResumes((current) => [...current, createdResume])
        setSelectedResume(String(createdResume.id))
        setResumeDraft({
          title: "",
          summary: "",
          skills: "",
        })
      } catch {
        // useCreateResume owns the visible error message.
      }
    },
    [createResume, resumeDraft],
  )

  if (!isOpen) return null

  if (statusLoading) {
    return (
      <div className={styles.dialogOverlay}>
        <div className={styles.dialogContent}>
          <StatusMessage
            tone="loading"
            title="Checking application status"
            message="We are checking whether you have already applied for this position."
          />
        </div>
      </div>
    )
  }

  if (statusError) {
    return (
      <div className={styles.dialogOverlay}>
        <div className={styles.dialogContent}>
          <StatusMessage
            tone="error"
            title="Could not check application status"
            message={statusError}
          />
        </div>
      </div>
    )
  }

  if (hasApplied) {
    return (
      <div className={styles.dialogOverlay}>
        <div className={styles.dialogContent}>
          <div className={styles.dialogHeader}>
            <h2 className={styles.dialogTitle}>Application Status</h2>
            <button onClick={onClose} className={styles.closeButton} aria-label="Close">
              <X size={24} />
            </button>
          </div>
          <div className={styles.dialogBody}>
            <StatusMessage
              tone="info"
              title="Already applied"
              message="You have already applied for this position."
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle}>Apply for Position</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label="Close">
            <X size={24} />
          </button>
        </div>
        <div className={styles.dialogBody}>
          {resumesLoading && (
            <StatusMessage
              tone="loading"
              title="Loading resumes"
              message="We are loading your resumes before you apply."
            />
          )}
          {resumesError && (
            <StatusMessage
              tone="error"
              title="Could not load resumes"
              message={resumesError}
            />
          )}
          {!resumesLoading && !resumesError && availableResumes.length === 0 && (
            <>
              <StatusMessage
                tone="empty"
                title="No resumes available"
                message="Create a resume before applying for this position."
              />
              <form onSubmit={handleCreateResume} aria-label="Create resume">
                <div className={styles.fieldGroup}>
                  <label htmlFor="resumeTitle" className={styles.fieldLabel}>
                    Resume Title
                  </label>
                  <input
                    id="resumeTitle"
                    name="title"
                    type="text"
                    value={resumeDraft.title}
                    onChange={handleResumeDraftChange}
                    className={styles.select}
                    required
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label htmlFor="resumeSummary" className={styles.fieldLabel}>
                    Summary
                  </label>
                  <textarea
                    id="resumeSummary"
                    name="summary"
                    value={resumeDraft.summary}
                    onChange={handleResumeDraftChange}
                    className={styles.textarea}
                    rows="3"
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label htmlFor="resumeSkills" className={styles.fieldLabel}>
                    Skills
                  </label>
                  <textarea
                    id="resumeSkills"
                    name="skills"
                    value={resumeDraft.skills}
                    onChange={handleResumeDraftChange}
                    className={styles.textarea}
                    rows="2"
                  />
                </div>
                <div className={styles.dialogFooter}>
                  <button type="button" onClick={onClose} className={styles.cancelButton}>
                    Cancel
                  </button>
                  <button type="submit" className={styles.applyButton} disabled={creatingResume}>
                    {creatingResume ? "Creating..." : "Create Resume"}
                  </button>
                </div>
              </form>
              {(createResumeError || createResumeResultError) && (
                <StatusMessage
                  tone="error"
                  title="Could not create resume"
                  message={createResumeError || createResumeResultError}
                />
              )}
            </>
          )}
          {!resumesLoading && !resumesError && availableResumes.length > 0 && (
            <form onSubmit={handleSubmit}>
              <div className={styles.fieldGroup}>
                <label htmlFor="resume" className={styles.fieldLabel}>
                  Select Resume
                </label>
                <select
                  id="resume"
                  value={selectedResume}
                  onChange={(e) => setSelectedResume(e.target.value)}
                  className={styles.select}
                  required
                >
                  <option value="">Select a resume</option>
                  {availableResumes.map((resume) => (
                    <option key={resume.id} value={resume.id}>
                      {resume.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="coverLetter" className={styles.fieldLabel}>
                  Cover Letter (Optional)
                </label>
                <textarea
                  id="coverLetter"
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  className={styles.textarea}
                  rows="5"
                />
              </div>
              <div className={styles.dialogFooter}>
                <button type="button" onClick={onClose} className={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" className={styles.applyButton} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </form>
          )}
          {submitError && (
            <StatusMessage
              tone="error"
              title="Could not submit application"
              message={submitError}
            />
          )}
          {submitSuccess && (
            <StatusMessage
              tone="info"
              title="Application submitted"
              message="Your application was submitted successfully."
            />
          )}
        </div>
      </div>
    </div>
  )
})

